/**
 * @dupe/generative-ui
 *
 * Shared types and schemas for generative UI report rendering.
 * Used by both the API (generation) and web (rendering) packages.
 */

// Version constants
export {
  CATALOG_VERSION,
  RENDERER_VERSION,
  THEME_VERSION,
  GENERATOR_VERSION,
} from './catalog'

// Report outline helpers
export {
  DEFAULT_REPORT_OUTLINE,
  type DefaultReportOutlineSection,
  canonicalizeReportSectionId,
  canonicalizeReportOutlineSections,
} from './reportOutline'

// Section/outline catalogs (versioned report structure)
export {
  SECTION_CATALOG_VERSION,
  REPORT_SECTION_IDS,
  ReportSectionIdSchema,
  type ReportSectionId,
  type ReportSectionDefinition,
  REPORT_SECTION_CATALOG,
  listReportSectionIds,
  listRequiredReportSections,
  getReportSectionDefinition,
} from './sectionCatalog'

export {
  OUTLINE_CATALOG_VERSION,
  OUTLINE_TEMPLATE_IDS,
  OutlineTemplateIdSchema,
  type OutlineTemplateId,
  type OutlineTemplate,
  type OutlineSection,
  OUTLINE_TEMPLATES,
  getOutlineTemplate,
  resolveOutlineSections,
  listOutlineTemplateIds,
  listOutlineTemplates,
  isSectionInTemplate,
} from './outlineCatalog'

// Schemas
export {
  // Data binding
  DataBindingPathSchema,
  type DataBindingPath,
  // Actions
  ActionIdSchema,
  ActionSchema,
  type ActionId,
  type Action,
  // Component props
  BoxPropsSchema,
  FlexPropsSchema,
  GridPropsSchema,
  SectionPropsSchema,
  HeadingPropsSchema,
  TextPropsSchema,
  LinkPropsSchema,
  ButtonPropsSchema,
  BadgePropsSchema,
  CheckboxPropsSchema,
  InputPropsSchema,
  LabelPropsSchema,
  TextareaPropsSchema,
  SelectPropsSchema,
  ProgressPropsSchema,
  SeparatorPropsSchema,
  ScrollAreaPropsSchema,
  ScrollBarPropsSchema,
  AvatarPropsSchema,
  ImagePropsSchema,
  SkeletonPropsSchema,
  CardPropsSchema,
  CardHeaderPropsSchema,
  CardTitlePropsSchema,
  CardDescriptionPropsSchema,
  CardContentPropsSchema,
  CardFooterPropsSchema,
  AlertPropsSchema,
  AlertTitlePropsSchema,
  AlertDescriptionPropsSchema,
  TablePropsSchema,
  TableHeaderPropsSchema,
  TableBodyPropsSchema,
  TableFooterPropsSchema,
  TableRowPropsSchema,
  TableHeadPropsSchema,
  TableCellPropsSchema,
  TableCaptionPropsSchema,
  AccordionPropsSchema,
  AccordionItemPropsSchema,
  AccordionTriggerPropsSchema,
  AccordionContentPropsSchema,
  TabsPropsSchema,
  TabsListPropsSchema,
  TabsTriggerPropsSchema,
  TabsContentPropsSchema,
  CarouselPropsSchema,
  CarouselItemPropsSchema,
  DialogPropsSchema,
  DialogTriggerPropsSchema,
  DialogContentPropsSchema,
  DialogHeaderPropsSchema,
  DialogTitlePropsSchema,
  DialogDescriptionPropsSchema,
  SheetPropsSchema,
  SheetContentPropsSchema,
  DrawerPropsSchema,
  PopoverPropsSchema,
  PopoverContentPropsSchema,
  TooltipPropsSchema,
  ProductCardPropsSchema,
  ProductGridPropsSchema,
  PriceDisplayPropsSchema,
  RatingDisplayPropsSchema,
  SpecsTablePropsSchema,
  ProsConsPropsSchema,
  ReportHeaderPropsSchema,
  ReportTopNavPropsSchema,
  ReportActionBarPropsSchema,
  ReportHeroPropsSchema,
  ReportRankedOptionsSectionPropsSchema,
  ReportHeadToHeadComparisonSectionPropsSchema,
  ReportResearchMetricsSectionPropsSchema,
  ReportAreaChartPropsSchema,
  TableOfContentsPropsSchema,
  ArticleSectionPropsSchema,
  VerdictPropsSchema,
  OurRecommendationTopPickSchema,
  OurRecommendationSmartPickSchema,
  OurRecommendationPropsSchema,
  QuickDecisionHelperPropsSchema,
  ReportFooterPropsSchema,
  CalloutPropsSchema,
  VideoEmbedPropsSchema,
  QuotePropsSchema,
  ComparisonTablePropsSchema,
  // Component type registry
  ComponentTypeSchema,
  ComponentPropsSchemaMap,
  type ComponentType,
  // UI Element and Tree
  UIElementSchema,
  UITreeSchema,
  type UIElement,
  type UITree,
  // Pinned payload
  PinnedUIPayloadSchema,
  type PinnedUIPayload,
  // Catalog schema
  CatalogSchemaSchema,
  type CatalogSchema,
  // Generator input
  GeneratorInputSchema,
  type GeneratorInput,
  // Validation helpers
  validateUITree,
  checkCircularReferences,
  createMinimalTree,
  normalizeBindingPath,
  // Example trees
  exampleProductReportTree,
} from './catalog'

// Re-export render utilities (to be added)
export * from './render'

// Research hub page contracts
export * from './hubs'
